import axios from 'axios';

export type FieldErrors = Record<string, string>;

const FIELD_PREFIXES = [
  'managementEmail',
  'managementPhone',
  'operatorId',
  'name',
  'address',
  'serviceFeeDay',
  'paymentFromDay',
  'paymentToDay',
  'feeCode',
  'buildingId',
  'basePrice',
  'vatPercentage',
  'vatAmount',
];

const GENERIC_HTTP_ERROR_LABELS = new Set([
  'bad request',
  'internal server error',
  'forbidden',
  'unauthorized',
  'not found',
]);

function isUsefulErrorText(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return !GENERIC_HTTP_ERROR_LABELS.has(trimmed.toLowerCase());
}

function extractResponseMessage(data: unknown): string | string[] | undefined {
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  const payload = data as Record<string, unknown>;

  if (typeof payload.message === 'string' || Array.isArray(payload.message)) {
    return payload.message as string | string[];
  }

  const nestedError = payload.error;
  if (typeof nestedError === 'string' && isUsefulErrorText(nestedError)) {
    return nestedError;
  }

  if (nestedError && typeof nestedError === 'object') {
    const errorObj = nestedError as Record<string, unknown>;
    if (typeof errorObj.message === 'string' || Array.isArray(errorObj.message)) {
      return errorObj.message as string | string[];
    }
  }

  return undefined;
}

function formatMessage(rawMessage: string | string[] | undefined, fallback: string): string {
  if (Array.isArray(rawMessage)) {
    return rawMessage.filter(Boolean).join(', ') || fallback;
  }

  if (typeof rawMessage === 'string' && rawMessage.trim()) {
    return rawMessage.trim();
  }

  return fallback;
}

export function parseApiFieldErrors(error: unknown): FieldErrors {
  if (!axios.isAxiosError(error)) {
    return {};
  }

  const rawMessage = extractResponseMessage(error.response?.data);
  const messages = Array.isArray(rawMessage)
    ? rawMessage
    : rawMessage
      ? [String(rawMessage)]
      : [];

  const fieldErrors: FieldErrors = {};

  for (const message of messages) {
    const field = FIELD_PREFIXES.find((name) => message.startsWith(`${name} `));
    if (field) {
      fieldErrors[field] = message;
    }
  }

  return fieldErrors;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const fromBody = formatMessage(extractResponseMessage(error.response?.data), '');
    if (fromBody && isUsefulErrorText(fromBody)) {
      return fromBody;
    }
    const status = error.response?.status;
    if (status) {
      return `${fallback} (HTTP ${status})`;
    }
    if (error.code === 'ECONNABORTED') {
      return `${fallback}. Hết thời gian chờ — thử gửi lại hoặc chia nhỏ dữ liệu.`;
    }
    if (error.message && isUsefulErrorText(error.message)) {
      return `${fallback}: ${error.message}`;
    }
    return fallback;
  }

  if (error instanceof Error && error.message && isUsefulErrorText(error.message)) {
    return `${fallback}: ${error.message}`;
  }

  return fallback;
}
